/**
 * AA (Account Aggregator) API Client
 *
 * API functions for interacting with the BFF AA endpoints.
 * Used by hooks and components for AA consent flow management.
 *
 * Endpoints:
 * - GET /v1/aa/status - Get user's AA consent status and linked accounts
 * - GET /v1/aa/redirect - Get redirect URL for Saafe consent flow
 *
 * The callback is handled via browser redirect, not API call.
 * Use parseAACallbackParams() to extract callback result from URL.
 */

import { api, unwrapResponse, ApiError } from "./api";

// ============================================================================
// Types
// ============================================================================

/**
 * Consent status values
 */
export type ConsentStatus =
  | "PENDING"
  | "ACTIVE"
  | "PAUSED"
  | "REVOKED"
  | "EXPIRED"
  | "REJECTED"
  | "FAILED";

/**
 * Linked account information
 */
export interface LinkedAccount {
  /** Type of financial information (e.g., MUTUAL_FUNDS, EQUITIES) */
  fiType: string;
  /** Financial Information Provider ID */
  fipId: string;
  /** Masked account number for display */
  maskedAccNumber: string;
  /** Reference number for the linked account */
  linkRefNumber: string;
  /** ISO datetime when account was linked */
  linkedAt: string;
}

/**
 * AA status response from BFF
 */
export interface AAStatus {
  /** Whether user has an active consent */
  hasActiveConsent: boolean;
  /** Whether user can currently fetch financial data */
  canFetch: boolean;
  /** Consent handle for revoke operations */
  consentHandle: string | null;
  /** Current consent status */
  consentStatus: ConsentStatus | null;
  /** ISO datetime when consent expires */
  consentExpiresAt: string | null;
  /** List of linked accounts */
  linkedAccounts: LinkedAccount[];
  /** Number of linked accounts */
  linkedAccountsCount: number;
  /** ISO datetime of last data fetch */
  lastDataFetchAt: string | null;
}

/**
 * Redirect response from BFF
 */
export interface AARedirectResponse {
  /** URL to redirect user for Saafe consent flow */
  redirectUrl: string;
}

/**
 * Options for getting redirect URL
 */
export interface AARedirectOptions {
  /** URL to return to after consent flow (default: current page) */
  returnUrl?: string;
  /** Financial information types to request (default: MUTUAL_FUNDS) */
  fiTypes?: string[];
}

/**
 * Valid FI types for AA consent.
 *
 * Note: Validation of fiTypes is performed server-side by the BFF.
 * This constant is exported for UI consumers to use for building
 * dropdowns, type checking, and client-side form validation hints.
 */
export const VALID_FI_TYPES = [
  "DEPOSIT",
  "TERM_DEPOSIT",
  "RECURRING_DEPOSIT",
  "SIP",
  "CP",
  "GOVT_SECURITIES",
  "EQUITIES",
  "BONDS",
  "DEBENTURES",
  "MUTUAL_FUNDS",
  "ETF",
  "IDR",
  "CIS",
  "AIF",
  "INSURANCE_POLICIES",
  "NPS",
  "INVIT",
  "REIT",
  "GSTR1_3B",
  "OTHER",
] as const;

export type FIType = (typeof VALID_FI_TYPES)[number];

/**
 * Parsed callback parameters from URL after Saafe redirect
 */
export interface AACallbackParams {
  /** Callback status: success or error */
  status: "success" | "error" | null;
  /** Number of linked accounts (on success) */
  linkedAccountsCount: number | null;
  /** Error message (on error) */
  error: string | null;
  /** Error code from Saafe (on error) */
  errorCode: string | null;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Parse a string to an integer, returning null if the result is NaN.
 */
function parseIntOrNull(value: string | null): number | null {
  if (value === null) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get user's AA consent status and linked accounts
 *
 * @returns AA status with consent info and linked accounts
 * @throws ApiError on request failure
 *
 * @example
 * ```ts
 * const status = await getAAStatus();
 * if (status.hasActiveConsent && status.canFetch) {
 *   console.log('Linked accounts:', status.linkedAccountsCount);
 * }
 * ```
 */
export async function getAAStatus(): Promise<AAStatus> {
  const response = await api.v1.aa.status.$get();
  return unwrapResponse<AAStatus>(response);
}

/**
 * Get redirect URL for initiating Saafe consent flow
 *
 * After getting the URL, redirect the user to it:
 * ```ts
 * const { redirectUrl } = await getAARedirectUrl({ returnUrl: '/portfolio' });
 * window.location.href = redirectUrl;
 * ```
 *
 * @param options - Optional redirect configuration
 * @returns Object containing the redirect URL
 * @throws ApiError on request failure (e.g., invalid FI types)
 *
 * @example
 * ```ts
 * // Default: request MUTUAL_FUNDS consent
 * const { redirectUrl } = await getAARedirectUrl();
 *
 * // Custom: request multiple FI types with custom return URL
 * const { redirectUrl } = await getAARedirectUrl({
 *   returnUrl: '/dashboard',
 *   fiTypes: ['MUTUAL_FUNDS', 'EQUITIES'],
 * });
 * ```
 */
export async function getAARedirectUrl(
  options?: AARedirectOptions
): Promise<AARedirectResponse> {
  const query: Record<string, string> = {};

  if (options?.returnUrl) {
    query.returnUrl = options.returnUrl;
  }

  if (options?.fiTypes && options.fiTypes.length > 0) {
    query.fiTypes = options.fiTypes.join(",");
  }

  const response = await api.v1.aa.redirect.$get({ query });
  return unwrapResponse<AARedirectResponse>(response);
}

/**
 * Revoke AA consent
 *
 * @param consentHandle - The consent handle to revoke
 * @throws ApiError on request failure
 */
export async function revokeAAConsent(consentHandle: string): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:3001";
  const response = await fetch(`${baseUrl}/v1/aa/revoke`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ consentHandle }),
  });
  if (!response.ok) {
    throw new ApiError("Failed to revoke consent", response.status);
  }
}

/**
 * Parse AA callback parameters from URL search params
 *
 * After Saafe consent flow, user is redirected to returnUrl with query params:
 * - Success: ?aa_status=success&linked_accounts=3
 * - Error: ?aa_status=error&aa_error=message&aa_error_code=CODE
 *
 * @param searchParams - URL search params (from useSearchParams or window.location.search)
 * @returns Parsed callback parameters
 *
 * @example
 * ```ts
 * // In React component
 * const [searchParams] = useSearchParams();
 * const callbackResult = parseAACallbackParams(searchParams);
 *
 * if (callbackResult.status === 'success') {
 *   toast.success(`Linked ${callbackResult.linkedAccountsCount} accounts!`);
 * } else if (callbackResult.status === 'error') {
 *   toast.error(callbackResult.error ?? 'Failed to link accounts');
 * }
 * ```
 */
export function parseAACallbackParams(
  searchParams: URLSearchParams | string
): AACallbackParams {
  const params =
    typeof searchParams === "string"
      ? new URLSearchParams(searchParams)
      : searchParams;

  const aaStatus = params.get("aa_status");
  const linkedAccounts = params.get("linked_accounts");
  const aaError = params.get("aa_error");
  const aaErrorCode = params.get("aa_error_code");

  return {
    status: aaStatus === "success" ? "success" : aaStatus === "error" ? "error" : null,
    linkedAccountsCount: parseIntOrNull(linkedAccounts),
    error: aaError,
    errorCode: aaErrorCode,
  };
}

/**
 * Check if URL contains AA callback parameters
 *
 * @param searchParams - URL search params
 * @returns true if URL contains aa_status param
 *
 * @example
 * ```ts
 * if (hasAACallbackParams(searchParams)) {
 *   const result = parseAACallbackParams(searchParams);
 *   // Handle callback...
 * }
 * ```
 */
export function hasAACallbackParams(
  searchParams: URLSearchParams | string
): boolean {
  const params =
    typeof searchParams === "string"
      ? new URLSearchParams(searchParams)
      : searchParams;

  return params.has("aa_status");
}

/**
 * Remove AA callback parameters from URL
 *
 * Useful for cleaning up URL after processing callback
 *
 * @param searchParams - URL search params
 * @returns New URLSearchParams without AA params
 *
 * @example
 * ```ts
 * const cleanParams = removeAACallbackParams(searchParams);
 * navigate({ search: cleanParams.toString() }, { replace: true });
 * ```
 */
export function removeAACallbackParams(
  searchParams: URLSearchParams | string
): URLSearchParams {
  // Clone the input to avoid mutating the original URLSearchParams.
  // parseAACallbackParams and hasAACallbackParams only read values, so no clone needed there.
  const params =
    typeof searchParams === "string"
      ? new URLSearchParams(searchParams)
      : new URLSearchParams(searchParams.toString());

  params.delete("aa_status");
  params.delete("linked_accounts");
  params.delete("aa_error");
  params.delete("aa_error_code");

  return params;
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Get user-friendly error message for AA errors
 */
export function getAAErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    if (error.status === 400) {
      return error.message || "Invalid request. Please check your input.";
    }
    return error.message || "Something went wrong.";
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred.";
}

// ============================================================================
// Query Keys (for TanStack Query)
// ============================================================================

/**
 * Query key factory for AA queries.
 * Use these for consistent cache key management.
 *
 * Note: Only 'status' is included. The 'redirect' endpoint is intentionally
 * excluded because it initiates a redirect flow and returns a one-time-use URL
 * that should not be cached.
 */
export const aaQueryKeys = {
  all: ["aa"] as const,
  status: () => [...aaQueryKeys.all, "status"] as const,
} as const;
